import { z } from 'zod'
import { FUNNEL_TARGETS } from '../enums/funnel-stage.enum.js'
import { leadStatsViewSchema } from '../enums/lead-stats-view.enum.js'
import { pipelineComparisonSchema, pipelineSpanSchema } from '../enums/pipeline-span.enum.js'
import { calendarDaySchema } from '../validators/temporal.validator.js'
import { PIPELINE_COUPLE_LISTS } from '../constants/pipeline.js'

/**
 * Which couples a pipeline shows (docs/pipeline-view.spec.md, "The split"):
 * `all`, `unlinked` (couples with no GoHighLevel lead), or one of the company's
 * lead stats pipelines by id. An id the company doesn't have shows all.
 */
export const pipelineSplitSchema = z.union([z.literal('all'), z.literal('unlinked'), z.string().uuid()])

/**
 * Which pipeline, view and weeks to show; each defaults when left out. `period`
 * is the span's newest two-week period, by its first day; `span` how many weeks
 * end with it (two when left out); `compare` what they are set against (the
 * weeks just before when left out).
 */
export const pipelineQuerySchema = z
  .object({
    split: pipelineSplitSchema.optional(),
    view: leadStatsViewSchema.optional(),
    period: calendarDaySchema.optional(),
    span: pipelineSpanSchema.optional(),
    compare: pipelineComparisonSchema.optional(),
  })
  .strict()
export type PipelineQuery = z.infer<typeof pipelineQuerySchema>

/** Turns the pipeline email on or off at a company without GoHighLevel. */
export const putPipelineEmailInputSchema = z.object({ on: z.boolean() }).strict()
export type PutPipelineEmailInput = z.infer<typeof putPipelineEmailInputSchema>

/**
 * The couples behind one of a venue's counts (`target`), or in one of the lists
 * its Pipeline page points at (`list`): one or the other, a page at a time. The
 * page size is fixed (`PIPELINE_COUPLES_PAGE_SIZE`), so there is no limit to
 * ask for; the first page when left out.
 */
export const pipelineCouplesQuerySchema = z
  .object({
    split: pipelineSplitSchema.optional(),
    view: leadStatsViewSchema.optional(),
    period: calendarDaySchema.optional(),
    span: pipelineSpanSchema.optional(),
    target: z.enum(FUNNEL_TARGETS).optional(),
    list: z.enum(PIPELINE_COUPLE_LISTS).optional(),
    page: z.coerce.number().int().positive().optional(),
  })
  .strict()
  .refine((q) => (q.target === undefined) !== (q.list === undefined), {
    message: 'Ask for one count’s couples (target) or one list’s (list): exactly one of the two.',
  })
export type PipelineCouplesQuery = z.infer<typeof pipelineCouplesQuerySchema>
